import { isConnectivityFailure } from '@/lib/service-failure';

/**
 * What a failed section lookup says on screen.
 *
 * In one place because two sections render the same failures and had drifted apart: a 429 from the
 * failed-attempt budget was reported as an outage in both, which tells a provider the portal is down
 * when what actually happened is that they mistyped a policy number a few times. A section states the
 * reason it was refused, or says the service is unavailable — never the second one for the first.
 *
 * The throttle wording matches the problem document `PolicyEndpoints` returns with the 429. It is
 * repeated here rather than read off the response because a section never renders an API error body
 * (see `SectionMessage`).
 */
export const POLICY_NOT_FOUND_MESSAGE =
  'No policy matches that policy number and date of birth. Check both values and try again.';

export const TOO_MANY_ATTEMPTS_MESSAGE =
  'Too many failed attempts. Please wait a few minutes and try again.';

const NOT_FOUND = 404;
const TOO_MANY_REQUESTS = 429;

export interface SectionFailure {
  readonly tone: 'empty' | 'error';
  readonly message: string;
}

/**
 * Turns a failed request into the section's message, or null when the section has nothing to say.
 *
 * A 404 is the honest "no such policy" answer and a 429 is the attempt budget saying so in its own
 * words. A failure to reach the backend at all is neither: PP-028 gives that one standardised message
 * for the whole portal, so the section stays quiet and `ServiceUnavailableBanner` speaks once instead
 * of every section repeating it. What is left — the backend answered, and something inside it failed —
 * is still the section's own degraded state (US-007).
 */
export function sectionFailure(
  error: unknown,
  status: number | undefined,
  unavailableMessage: string,
): SectionFailure | null {
  if (status === NOT_FOUND) {
    return { tone: 'empty', message: POLICY_NOT_FOUND_MESSAGE };
  }

  if (status === TOO_MANY_REQUESTS) {
    return { tone: 'error', message: TOO_MANY_ATTEMPTS_MESSAGE };
  }

  return isConnectivityFailure(error) ? null : { tone: 'error', message: unavailableMessage };
}
